import { useRef, useState } from 'react';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import * as api from '../../services/taOutreachApi';
import type { OutreachCandidate } from '../../types/taOutreach';

type Template = Parameters<typeof api.draftOutreach>[1];

/**
 * Writing drafts on the page (moved out of CampaignView, Nov 2 2026): `draftOne` for a card's
 * Draft / Redraft / template switch and every loop, `queueAndDraft` for the auto-draft. A row whose
 * draft is already being written on THIS page is not asked twice (the auto-draft runs beside the
 * other loops since Nov 2 2026); across tabs and recruiters the proxy's claim does that.
 * `drafting` = the rows with a request open (the card's spinner).
 */
export function useDrafting(replace: (item: OutreachCandidate) => void) {
  const [drafting, setDrafting] = useState<Set<string>>(new Set());
  const inFlight = useRef(new Set<string>());
  const queueing = useRef(new Set<string>());
  const mark = (id: string, on: boolean) => {
    if (on) inFlight.current.add(id);
    else inFlight.current.delete(id);
    setDrafting(new Set(inFlight.current));
  };

  async function draftOne(id: string, template?: Template, { auto = false }: { auto?: boolean } = {}): Promise<OutreachCandidate | null> {
    if (inFlight.current.has(id)) return null;
    let done: OutreachCandidate | null = null;
    mark(id, true);
    try {
      const r = await api.draftOutreach(id, template, { auto });
      replace(r.item);
      done = r.item;
      // Drafted by a colleague meanwhile: nothing was written here.
      if (r.skipped) return done;
      void logAnalyticsEvent('ta_outreach_drafted', {
        outcome: r.item.draft ? 'drafted' : r.refusal ? 'refused' : 'failed',
        unknown_count: r.item.gap?.criteria.filter((c) => c.verdict === 'unknown').length ?? 0,
        contradicted_count: r.item.gap?.criteria.filter((c) => c.verdict === 'contradicted').length ?? 0,
        has_question: Boolean(r.item.gap?.question),
        facts: r.item.enrichment?.facts?.length ?? 0,
        auto_approved: r.item.status === 'ready' && Boolean(r.item.autoApproved),
      });
    } catch (err) {
      // A colleague's page is writing this one right now (the proxy's claim, Oct 28 2026).
      const busy = err instanceof api.OutreachApiError && err.body.code === 'busy';
      showToast({ status: busy ? 'info' : 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not draft.' });
    } finally {
      mark(id, false);
    }
    return done;
  }

  /** Queue (when not yet) and draft: the profile is read already. `auto`: approved by itself when clean. */
  async function queueAndDraft(id: string, queued: boolean, { auto = false }: { auto?: boolean } = {}) {
    // "Read profiles" and the auto-draft can reach the same row at once: one of them does it.
    if (queueing.current.has(id) || inFlight.current.has(id)) return;
    if (!queued) {
      queueing.current.add(id);
      try {
        replace((await api.moveOutreach(id, 'queue')).item);
      } catch (err) {
        showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not add to outreach.' });
        return;
      } finally {
        queueing.current.delete(id);
      }
    }
    await draftOne(id, undefined, { auto });
  }

  return { drafting, draftOne, queueAndDraft };
}
